import moment from 'moment-timezone';

import { DATE_FORMAT } from '@core/time-legacy';

// The date is a capture group of its own so that an ISO *timestamp* is replaced
// whole — matching only the date prefix would leave "Feb 19T00:00:00Z" behind.
const ISO_DATE =
  /(\d{4}-\d{2}-\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?/g;

// Parsed and rendered in UTC on both ends: a report window boundary is a plain
// calendar date, so it must read back as written. Plain `moment()` would land on
// midnight in the account timezone that `setDefaultTimezone` installs, and an
// `Intl` formatter with no `timeZone` renders in the browser's — a browser west
// of the account then shows the day before.
const toShortDate = (iso: string, withYear: boolean): string => {
  const date = moment.utc(iso, DATE_FORMAT, true);
  if (!date.isValid()) return iso;

  return new Intl.DateTimeFormat(moment.locale(), {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(date.toDate());
};

/**
 * The tool description asks the agent for a short footnote ("vs last month",
 * "vs Jan 15 – Apr 14"), but it sometimes echoes the raw ISO range it queried.
 * That string overflows the footnote's two-line clamp and misaligns the tile
 * row, so the dates are shortened here — the layout then holds whatever the
 * model sends. Anything without an ISO date passes through untouched.
 *
 * A repeated year is noise: it stays on the last date only, and disappears
 * entirely for a range that sits inside the current year.
 */
export function shortenIsoDates(text: string): string {
  const matches = text.match(ISO_DATE);
  if (!matches) return text;

  const years = new Set(matches.map(match => match.slice(0, 4)));
  const mixedYears = years.size > 1;
  const inCurrentYear = !mixedYears && years.has(String(new Date().getFullYear()));

  let remaining = matches.length;
  return text.replace(ISO_DATE, (_match, date: string) => {
    remaining -= 1;
    return toShortDate(date, mixedYears || (!inCurrentYear && remaining === 0));
  });
}
